import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  CalculateProcessorCapacityDocument,
  type CalculateProcessorCapacityQuery,
  type CalculateProcessorCapacityQueryVariables,
  EnumWaitlistStatus,
  type MyWaitlistQuery,
  useMyWaitlistQuery,
} from '../../../../lib/graphql';

/** How often the openings are asked again — the nav badge's poll. */
const OPENINGS_REFRESH_MS = 60_000;

export type WaitlistEntry = NonNullable<
  NonNullable<MyWaitlistQuery['myWaitlist']>[number]
>;

/**
 * Settled entries: already booked, or past their date window. The card draws
 * the same distinction for its chip and Schedule button — kept here as one
 * predicate so a booked entry can't be inert on the card and "waiting" in the
 * count above it.
 * @param entry - A waitlist entry.
 * @returns True when nothing more can happen on this entry.
 */
export function isWaitlistHistory(entry: WaitlistEntry): boolean {
  if (entry.status === EnumWaitlistStatus.Completed) return true;
  const endDate = moment.utc(entry.endDate as string);
  return endDate.isValid() && endDate.isBefore(moment.utc().startOf('day'));
}

/**
 * The capacity question for one entry: are there spots at this plant, for
 * this species, inside the dates they asked for?
 *
 * One builder for the card and the count, so the two ask in exactly the same
 * words — which is also what lets them share a cache entry.
 * @param entry - A waitlist entry.
 * @returns The query variables.
 */
export function waitlistCapacityVariables(
  entry: WaitlistEntry,
): CalculateProcessorCapacityQueryVariables {
  return {
    vendorId: entry.vendor as string,
    selectedAnimalSpecies: entry.animal as string,
    // Read in UTC so the stored midnight-UTC date isn't shifted to the
    // previous calendar day west of UTC. The resolver parses these via
    // `new Date(\`${date}T00:00:00.000Z\`)` so ISO date format is required;
    // the `dateFormat` arg only affects output.
    startDate: moment.utc(entry.startDate as string).format('YYYY-MM-DD'),
    endDate: moment.utc(entry.endDate as string).format('YYYY-MM-DD'),
    dateFormat: 'M/DD/YYYY',
  };
}

/**
 * How many of the producer's waitlist entries have a spot open right now.
 *
 * The one waitlist state that is a task: a date they asked for has come up,
 * and it goes to whoever books it first. Entries still full are waiting, not
 * work, and stay off the Tasks list.
 *
 * Capacity is a per-entry question with no batch form, so this asks once per
 * live entry — a handful at most; nobody is on fifty waitlists. It asks again
 * every {@link OPENINGS_REFRESH_MS}, the nav badge's cadence, because a spot
 * opening is exactly the change it exists to notice.
 *
 * Mount it ONCE — the portal shell does, and hands the number down through
 * {@link WaitlistOpeningsContext}. Read it elsewhere with
 * {@link useWaitlistOpeningsCount}, which doesn't ask again.
 * @param skip - Don't fetch (e.g. a buyer, who has no waitlist).
 * @returns The number of entries with an opening.
 */
export function useWaitlistOpenings(skip = false): number {
  const client = useApolloClient();
  const { data } = useMyWaitlistQuery({
    fetchPolicy: 'cache-and-network',
    skip,
  });

  const live = useMemo(
    () =>
      (data?.myWaitlist ?? []).filter(
        (entry): entry is WaitlistEntry =>
          Boolean(entry) &&
          Boolean(entry?.vendor) &&
          Boolean(entry?.animal) &&
          !isWaitlistHistory(entry as WaitlistEntry),
      ),
    [data?.myWaitlist],
  );
  // Refetch only when the SET of live entries changes, not on every poll
  // that returns the same list in a new array.
  const liveKey = live.map((entry) => String(entry._id)).join(',');

  const [openings, setOpenings] = useState(0);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (skip) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), OPENINGS_REFRESH_MS);
    return () => clearInterval(id);
  }, [skip]);
  useEffect(() => {
    if (skip || live.length === 0) {
      setOpenings(0);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      live.map((entry) =>
        client
          .query<CalculateProcessorCapacityQuery>({
            query: CalculateProcessorCapacityDocument,
            variables: waitlistCapacityVariables(entry),
            // Fresh on each ask, and written to the cache the waitlist cards
            // read from, so a card rendered after it doesn't ask again.
            fetchPolicy: 'network-only',
          })
          .then((res) =>
            Boolean(res.data?.calculateProcessorCapacity?.hasAvailableSpots),
          )
          // One plant failing to answer shouldn't blank the others.
          .catch(() => false),
      ),
    ).then((results) => {
      if (!cancelled) setOpenings(results.filter(Boolean).length);
    });
    return () => {
      cancelled = true;
    };
    // `live` is tracked through `liveKey`, which only changes with the set.
  }, [client, liveKey, skip, tick]);

  return openings;
}

/**
 * The shell's {@link useWaitlistOpenings} result. `null` outside the shell.
 */
export const WaitlistOpeningsContext = createContext<number | null>(null);

/**
 * The waitlist openings count, from the shell when it provides one; asks for
 * itself only when rendered outside it.
 * @param skip - Don't count (a buyer has no waitlist).
 * @returns The number of entries with an opening.
 */
export function useWaitlistOpeningsCount(skip = false): number {
  const provided = useContext(WaitlistOpeningsContext);
  const own = useWaitlistOpenings(skip || provided !== null);
  if (skip) return 0;
  return provided ?? own;
}
